import { useCallback, useEffect, useEffectEvent, useState } from 'react';
import { classifyAdminFailure, isAbortError, type AdminFailure } from './api';

export interface AdminRead<T> {
  /** The last answer received, possibly for an earlier key while the current one loads. */
  data: T | null;
  /** The key `data` belongs to; equal to the current key once its answer arrived. */
  dataKey: string | null;
  loading: boolean;
  failure: AdminFailure | null;
  /** Reads the current key again (after a failure). */
  retry: () => void;
}

interface State<T> {
  data: T | null;
  dataKey: string | null;
  loading: boolean;
  failure: AdminFailure | null;
}

/**
 * One staff read per key. A new key aborts the request of the previous one, so a slow answer to an
 * older search or page can never replace a newer one. The previous answer stays available while the new
 * one loads (pages keep showing it, marked busy) and is dropped on a failure, so stale rows are never
 * shown next to an error. A null key reads nothing (for example an address that cannot be an
 * organization).
 */
export function useAdminRead<T>(key: string | null, read: (signal: AbortSignal) => Promise<T>): AdminRead<T> {
  const [state, setState] = useState<State<T>>({
    data: null,
    dataKey: null,
    loading: key !== null,
    failure: null,
  });
  const [attempt, setAttempt] = useState(0);
  const runRead = useEffectEvent(read);

  useEffect(() => {
    if (key === null) {
      setState({ data: null, dataKey: null, loading: false, failure: null });
      return;
    }
    const controller = new AbortController();
    setState((current) => ({ ...current, loading: true, failure: null }));
    runRead(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setState({ data, dataKey: key, loading: false, failure: null });
      },
      (error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        setState({ data: null, dataKey: null, loading: false, failure: classifyAdminFailure(error) });
      },
    );
    return () => controller.abort();
  }, [key, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}
